import { getEntityRouteParam } from './entityIdentifier';

let lastBreadcrumbMeta = null;

const trimTrailingSlashes = path => {
  let end = path.length;
  while (end > 0 && path[end - 1] === '/') end--;
  return path.slice(0, end);
};

const normalizeResourcePath = resource => {
  if (!resource) return '/schemas';
  return trimTrailingSlashes(String(resource).split('?')[0]) || '/schemas';
};

/**
 * Parses a native UI resource path to extract the page type and schema ID.
 * Resolves to { type: 'record' | 'object' | 'schema' | 'list', schemaId?: string }
 */
const parseResourcePath = resource => {
  const path = normalizeResourcePath(resource);

  let match = path.match(/^\/schemas\/([^/]+)\/records\/([^/]+)$/);
  if (match) return { type: 'record', schemaId: match[1] };

  match = path.match(/^\/schemas\/([^/]+)\/records$/);
  if (match) return { type: 'object', schemaId: match[1] };

  match = path.match(/^\/schemas\/edit\/([^/]+)$/);
  if (match) return { type: 'schema', schemaId: match[1] };

  if (path === '/schemas/create') return { type: 'schema' };

  return { type: 'list' };
};

/**
 * Classifies a native UI resource path so callers can decide whether to keep
 * or clear the remembered breadcrumb meta.
 */
export const classifyNativeUiResource = resource => parseResourcePath(resource).type;

/**
 * Remembers object/record breadcrumb meta across in-iframe navigations.
 * List and create routes clear it. Edit/object/record emits reuse the last
 * named object (e.g. while loading) if it belongs to the schema in the route.
 */
export const resolveRouteChangeMeta = (resource, meta) => {
  const { type, schemaId } = parseResourcePath(resource);

  // Clear state for lists or routes without a schema ID (like create)
  if (type === 'list' || !schemaId) {
    lastBreadcrumbMeta = null;
    return null;
  }

  // Update state if valid meta with a name is provided
  if (meta?.object) {
    if (meta.object.name) {
      lastBreadcrumbMeta = meta;
    }
    return meta;
  }

  // Fallback to previous state if the schema matches the current route
  if (lastBreadcrumbMeta && String(lastBreadcrumbMeta.object?.id) === String(schemaId)) {
    return { object: lastBreadcrumbMeta.object };
  }

  return null;
};

export const resetLastRouteChangeMeta = () => {
  lastBreadcrumbMeta = null;
};

/**
 * Builds ROUTE_CHANGE meta for the host breadcrumb bar.
 */
export function buildRouteChangeMeta({ entity, record, displayId, routeParam, showRecordId = false } = {}) {
  const entityName = entity?.title || entity?.name;
  if (!entityName) return null;

  const param = routeParam ?? getEntityRouteParam(entity);
  if (!param) return null;

  const objectPath = `/schemas/${param}/records`;
  const meta = {
    object: {
      name: entityName,
      id: String(param),
      path: objectPath,
    },
  };

  const recordId = displayId ?? record?.display_id ?? record?.id;
  if (recordId != null && recordId !== '') {
    const primaryFieldName = record?.metadata?.primary_field_name;
    const primaryValue = primaryFieldName ? record?.data?.[primaryFieldName] : null;
    const hasPrimary = !showRecordId && primaryValue != null && String(primaryValue).trim() !== '';
    meta.record = {
      id: String(recordId),
      name: hasPrimary ? String(primaryValue) : String(recordId),
      path: `${objectPath}/${recordId}`,
    };
  }

  return meta;
}
